import type { ReactNode } from "react";

import { FaultLinkPanel } from "../components/FaultLinkPanel";
import { HarmonicForm } from "../components/HarmonicForm";
import { HarmonicResultPanel } from "../components/HarmonicResultPanel";
import { HarmonicResultSummary } from "../components/HarmonicResultSummary";
import { LoadLinkPanel } from "../components/LoadLinkPanel";
import { PowerQualityWarningPanel } from "../components/PowerQualityWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useHarmonics } from "../hooks/useHarmonics";
import { useSldElements } from "../hooks/useSldElements";
import type { HarmonicAssessmentRunCreateRequest } from "../services/harmonics";
import { downloadRunJson } from "../services/runExport";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The harmonic assessment could not be completed.";
}

type HarmonicPageProps = {
  modePicker?: ReactNode;
};

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, the three links, warnings, traceability, then the detail tables. This
// page is EOS-11b, the second mode of the power quality page.
export function HarmonicPage({ modePicker }: HarmonicPageProps = {}) {
  const { calculate, reset, result, run, error, isPending, isError } = useHarmonics();
  const { elements: sldElements } = useSldElements();

  async function handleSubmit(payload: HarmonicAssessmentRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-harmonic-page>
      <header>
        <p>EOS-11 · Power factor and harmonics</p>
        <h1>Harmonic assessment</h1>
        <p>
          Assess the distortion at one point of common coupling from the harmonic spectra the
          engineer declares with their sources: THDi, TDD over the maximum demand current, the
          harmonic voltages and THDv from the short-circuit power of a stored fault study, and
          Isc / IL. A limit is compared only against a verified register table or the limit
          declared with its source; the product ships no typical spectrum and no limit of its
          own.
        </p>
        {modePicker}
      </header>

      <div data-study-layout>
        <section aria-labelledby="harmonic-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="harmonic-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <HarmonicForm disabled={isPending} onSubmit={handleSubmit} sldElements={sldElements} />
          </details>
        </section>

        <section aria-labelledby="harmonic-results-heading" data-study-results>
          <h2 id="harmonic-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Assessing the harmonics…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <HarmonicResultSummary result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              {run ? <LoadLinkPanel link={run.load_link} /> : null}
              {run ? <FaultLinkPanel link={run.fault_link} /> : null}
              <PowerQualityWarningPanel warnings={result.warnings} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <HarmonicResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to assess the harmonics.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="harmonic-review-heading">
        <h2 id="harmonic-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance
          declaration. Confirm every declared spectrum against its source, the demand current,
          the linked fault study and the declared limit's source before approval or issue; a
          comparison against a declared limit is not a verified limit check.
        </p>
      </section>
    </main>
  );
}

export default HarmonicPage;
